/**
 * Централизованный API сервис
 * Предоставляет унифицированные методы для всех запросов к серверу
 */

import API_CONFIG from './config';

/**
 * Базовые заголовки для всех запросов
 */
const baseHeaders = {
  'Content-Type': 'application/json',
};

/**
 * Базовая функция для обработки ошибок
 * @param {string} endpoint - Endpoint, к которому был сделан запрос
 * @param {Error} error - Ошибка
 * @throws {Error} Перебрасывает ошибку после логирования
 */
function handleError(endpoint, error) {
  console.error(`API Request failed to ${endpoint}:`, error);
  throw error;
}

/**
 * Базовая функция для выполнения запросов с JSON body
 * @param {string} endpoint - Endpoint (например, 'login')
 * @param {Object} options - Опции fetch
 * @returns {Promise<any>} Результат запроса в формате JSON
 */
async function apiRequest(endpoint, options = {}) {
  const url = API_CONFIG.getEndpointUrl(endpoint);
  
  const config = {
    ...options,
    headers: {
      ...baseHeaders,
      ...(options.headers || {}),
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Проверяем, можно ли прочитать как JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    return await response.text();
  } catch (error) {
    handleError(endpoint, error);
  }
}

/**
 * Выполнение запроса с FormData (для загрузки файлов)
 * @param {string} endpoint - Endpoint
 * @param {FormData} formData - Данные в формате FormData
 * @returns {Promise<any>} Результат запроса
 */
async function apiRequestFormData(endpoint, formData) {
  const url = API_CONFIG.getEndpointUrl(endpoint);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    handleError(endpoint, error);
  }
}

/**
 * GET запрос
 * @param {string} endpoint - Endpoint
 * @param {Object} params - Параметры для URL query string
 * @returns {Promise<any>} Результат запроса
 */
export const apiGet = async (endpoint, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiRequest(`${endpoint}${queryString ? '?' + queryString : ''}`, {
    method: 'GET',
  });
};

/**
 * POST запрос с JSON body
 * @param {string} endpoint - Endpoint
 * @param {Object} body - Тело запроса
 * @returns {Promise<any>} Результат запроса
 */
export const apiPost = async (endpoint, body) => {
  return apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

/**
 * PUT запрос с JSON body
 * @param {string} endpoint - Endpoint
 * @param {Object} body - Тело запроса
 * @returns {Promise<any>} Результат запроса
 */
export const apiPut = async (endpoint, body) => {
  return apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

/**
 * DELETE запрос
 * @param {string} endpoint - Endpoint
 * @returns {Promise<any>} Результат запроса
 */
export const apiDelete = async (endpoint) => {
  return apiRequest(endpoint, {
    method: 'DELETE',
  });
};

/**
 * POST запрос с FormData (для загрузки файлов)
 * @param {string} endpoint - Endpoint
 * @param {FormData} formData - Данные в формате FormData
 * @returns {Promise<any>} Результат запроса
 */
export const apiPostFormData = async (endpoint, formData) => {
  return apiRequestFormData(endpoint, formData);
};

/**
 * Создает FormData из объекта
 * @param {Object} data - Объект для преобразования
 * @returns {FormData} Сформированный FormData
 */
export const createFormData = (data) => {
  const formData = new FormData();
  Object.keys(data).forEach(key => {
    if (data[key] instanceof File || data[key] instanceof Blob) {
      formData.append(key, data[key]);
    } else {
      formData.append(key, data[key]);
    }
  });
  return formData;
};

/**
 * Создает FormData из массива файлов
 * @param {string} fieldName - Имя поля в FormData
 * @param {File[]} files - Массив файлов
 * @returns {FormData} Сформированный FormData
 */
export const createFilesFormData = (fieldName, files) => {
  const formData = new FormData();
  files.forEach((file, index) => {
    formData.append(`${fieldName}[${index}]`, file);
  });
  return formData;
};

// Экспорт всех методов как объект API
export default {
  /**
   * GET запрос
   * @param {string} endpoint - Endpoint
   * @param {Object} params - Параметры URL
   */
  get: apiGet,
  
  /**
   * POST запрос с JSON body
   * @param {string} endpoint - Endpoint
   * @param {Object} body - Тело запроса
   */
  post: apiPost,
  
  /**
   * PUT запрос с JSON body
   * @param {string} endpoint - Endpoint
   * @param {Object} body - Тело запроса
   */
  put: apiPut,
  
  /**
   * DELETE запрос
   * @param {string} endpoint - Endpoint
   */
  delete: apiDelete,
  
  /**
   * POST запрос с FormData
   * @param {string} endpoint - Endpoint
   * @param {FormData} formData - Данные в формате FormData
   */
  postFormData: apiPostFormData,
  
  /**
   * Создает FormData из объекта
   * @param {Object} data - Объект для преобразования
   */
  createFormData,
  
  /**
   * Создает FormData из массива файлов
   * @param {string} fieldName - Имя поля
   * @param {File[]} files - Массив файлов
   */
  createFilesFormData,
};
